import React, {Dispatch, useCallback, useEffect, useMemo, useReducer} from "react";
import _ from "lodash";
import {addSeconds, endOfDay, isAfter, isBefore, startOfDay} from 'date-fns';
import { DateUtils, Interval } from "../../../core/utils/dateUtils";
import { createServiceProvider } from "../../../context/AppServiceProvider";
import { RoundScheduleItem, RoundUtils, RoundUtilStores } from "../../../syncstream/utils/RoundUtils";
import { useSyncCenter } from "../../../syncstream/SyncCenterProvider";
import { useMap } from "../../../core/storage/hooks/UseStore";
import {Logger} from "../../../core/logger/logger";

interface Parameters {
    // Stores used to calculate result
    stores: RoundUtilStores;
    interval: Interval;
    facilityIds: number[];
    notBefore: Date | undefined;
}
const logger = new Logger('GetSyringeDriverSchedule');

interface NeedNewRoundScheduleProps {
    cachedParameters?: Parameters | null;
    requestedParameters: Parameters;
}
function needNewRoundSchedule(props: NeedNewRoundScheduleProps): boolean {
    const {cachedParameters, requestedParameters} = props;
    if (!cachedParameters) {
        return true;
    }
    if (!!cachedParameters.notBefore && new Date() < cachedParameters.notBefore) {
        // Too soon.  Ignore.
        logger.info("Deferring re-calculation of cache until ", cachedParameters.notBefore);
        return false;
    }
    if (!_.isEqual(cachedParameters.stores, requestedParameters.stores) ||
        !_.isEqual(cachedParameters.facilityIds, requestedParameters.facilityIds)) {
        return true;
    }

    const cacheStart = DateUtils.toDate(cachedParameters.interval.start);
    const cacheEnd = DateUtils.toDate(cachedParameters.interval.end);
    const requestedStart = DateUtils.toDate(requestedParameters.interval.start);
    const requestedEnd = DateUtils.toDate(requestedParameters.interval.end);

    return isBefore(requestedStart, cacheStart) || isAfter(requestedEnd, cacheEnd);
}

interface State {
    parameters: Parameters | null;
    result: RoundScheduleItem[];
}

const initialState: State = {
    parameters: null,
    result: []
};

type ContextValue = [State, Dispatch<Action>];

const [Provider, useHook] = createServiceProvider<ContextValue>();

interface ReplaceStateAction {
    type: "REPLACE" | "DEFER"  | "CLEAR_DEFERRAL";
    payload: State;
}

type Action = ReplaceStateAction;

function reducer(state: State, action: Action): State {
    if (action.type === "REPLACE") {
        state = action.payload;
    }
    if (action.type === "DEFER") {
        state = action.payload;
    }
    if (action.type === "CLEAR_DEFERRAL" && !!state.parameters?.notBefore) {
        state = action.payload;
    }
    return state;
}

interface GetSyringeDriverScheduleProviderProps {
    children: React.ReactNode;
}
export function GetSyringeDriverScheduleProvider(props: GetSyringeDriverScheduleProviderProps) {
    const value = useReducer(reducer, initialState);
    const {children} = props;
    useEffect(() => {
        const now = new Date();
        const notBefore = value[0].parameters?.notBefore;
        if (notBefore && notBefore > now) {
            const ms = notBefore.getTime() - now.getTime();
            const timerId = setTimeout(() => {
                value[1]({type : "CLEAR_DEFERRAL", payload: value[0]});
            }, ms);
            return () => clearTimeout(timerId);
        }
    }, [value]);

    return (
        <Provider value={value}>
            {children}
        </Provider>
    );
}
export function useDelaySchedule() {
    const contextValue = useHook();
    return useCallback((delayInSeconds: number) => {
        const [state, dispatch] = contextValue;
        const newState = {...state}
        if (!newState.parameters) {
            return;
        }
        if (!delayInSeconds) {
            newState.parameters.notBefore = undefined;
            dispatch({type: "CLEAR_DEFERRAL", payload: newState});
        }
        else {
            const newTm = addSeconds(new Date(), delayInSeconds);
            if (!newState.parameters.notBefore || newState.parameters.notBefore < newTm) {
                newState.parameters.notBefore = newTm;
                dispatch({type: "DEFER", payload: newState});
            }
        }
    }, [contextValue]);
}

function useRoundUtilStore(): RoundUtilStores {
    const services = useSyncCenter();

    const packedDayStore = useMap(services.packedPatientDays);
    const patientStore = useMap(services.patients);
    const drugStore = useMap(services.drugs);
    const packedPrnStore = useMap(services.packedPatientPrnMedications);
    const roundStore = useMap(services.rounds);
    const facilityStore = useMap(services.facilities);
    const facilityGroupConfigurationStore = useMap(services.facilityGroupConfigurations);
    const syringeDriverRunStore = useMap(services.syringeDriverRun);
    const patchApplicationStore = useMap(services.patchApplications);

    return useMemo(() => ({
        packedDayStore,
        patientStore,
        drugStore,
        packedPrnStore,
        roundStore,
        facilityStore,
        facilityGroupConfigurationStore,
        syringeDriverRunStore,
        patchApplicationStore
    }), [
        packedDayStore,
        patientStore,
        drugStore,
        packedPrnStore,
        roundStore,
        facilityStore,
        facilityGroupConfigurationStore,
        syringeDriverRunStore
    ]);
}

// This returns a function that can be called from within a side-effect or a render method
export function useGetSyringeScheduleFromParameters() {
    const contextValue = useHook();
    const stores = useRoundUtilStore();
    return useCallback((interval: Interval, facilityIds: number[]) => {
        return getScheduleFromParameters(interval, facilityIds, stores, contextValue);
    }, [contextValue, stores]);
}
export function useDelaySyringeDriverSchedule() {
    const contextValue = useHook();
    return useCallback((delayInSeconds: number) => {
        const [state, dispatch] = contextValue;
        const newState = {...state}
        if (!newState.parameters) {
            return;
        }
        if (!delayInSeconds) {
            newState.parameters.notBefore = undefined;
            dispatch({type: "CLEAR_DEFERRAL", payload: newState});
        }
        else {
            const newTm = addSeconds(new Date(), delayInSeconds);
            if (!newState.parameters.notBefore || newState.parameters.notBefore < newTm) {
                newState.parameters.notBefore = newTm;
                dispatch({type: "DEFER", payload: newState});
            }
        }
    }, [contextValue]);

}

function getScheduleFromParameters(interval: Interval, facilityIds: number[], stores: RoundUtilStores, contextValue: ContextValue) {
    const dayParameters = getParametersForDay(interval, facilityIds, stores, contextValue[0].parameters?.notBefore ?? undefined);
    const dayItems = getScheduleForDay(dayParameters, contextValue);
    return filterItems(dayItems, interval);
}

function filterItems(items: RoundScheduleItem[], interval: Interval): RoundScheduleItem[] {
    const filteredItems: RoundScheduleItem[] = [];
    const dateInterval = DateUtils.intervalToDateInterval(interval);

    for (const item of items) {
        const timestamp = getTimestamp(item);
        if (isBefore(timestamp, dateInterval.start)) {
            continue;
        }
        if (isAfter(timestamp, dateInterval.end)) {
            continue;
        }
        filteredItems.push(item);
    }

    return filteredItems;
}

function getTimestamp(item: RoundScheduleItem): Date {
    return DateUtils.toDate(item.syringeDriverRunActivity!.doseTimestamp);
}

function getParametersForDay(interval: Interval, facilityIds: number[], stores: RoundUtilStores, notBefore: Date | undefined): Parameters {
    const dateInterval = DateUtils.intervalToDateInterval(interval);
    dateInterval.start = startOfDay(dateInterval.start);
    // now we are getting tomorrow's data as well
    dateInterval.end = endOfDay(dateInterval.end);
      // if (Math.random() > 0.5) {
      //     console.log("Adding an Extra Day");
      //     dateInterval.end = addDays(dateInterval.end, 1);
      // }

    const dayInterval: Interval = {
        start: DateUtils.fromDate(dateInterval.start),
        end: DateUtils.fromDate(dateInterval.end)
    };

    facilityIds = [...facilityIds];
    facilityIds.sort((id1, id2) => id1 - id2);

    stores = { ...stores };

    return {
        stores,
        facilityIds,
        interval: dayInterval,
        notBefore: notBefore
    };
}

// getScheduleForDay gets called multiple times concurrently.  This tempState is a rather pathetic
// attempt to stop this recalculating.
let tempState: State | null = null;
function getScheduleForDay(parameters: Parameters, contextValue: ContextValue) {
    const [state, dispatch] = contextValue;

    if (!needNewRoundSchedule({cachedParameters: state.parameters, requestedParameters: parameters})) {
        return state.result;
    }

    if (tempState && !needNewRoundSchedule({cachedParameters: tempState!.parameters, requestedParameters: parameters})) {
        return tempState!.result;
    }
    logger.info("Recomputing syringe driver run daily schedule");
    const roundUtils = new RoundUtils(parameters.stores);
    const result = roundUtils.getSyringeDriverRunScheduleFromParameters(parameters.interval, parameters.facilityIds);
    tempState = {
        parameters: {...parameters},
        result: [...result]
    }
    const newState = {
        parameters: {...parameters},
        result: [...result]
    };
    setTimeout(() => dispatch({type: "REPLACE", payload: newState}));
    setTimeout(() => tempState = null);
    logger.info("Daily schedule recomputed");
    return result;
}
 